// src/components/SaveSurfaceProjectModal.js
import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import API_BASE from "../api/apiBase";

export default function SaveSurfaceProjectModal({
  isOpen,
  onClose,
  measurements,
  canvasRef,
}) {
  const [projectName, setProjectName] = useState("");
  const [surfaceName, setSurfaceName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  // Reset when opening
  useEffect(() => {
    if (!isOpen) return;
    setErrMsg(null);
    setLoading(false);
    setProjectName("");
    setSurfaceName("");
    setPhone("");
    setNotes("");
  }, [isOpen]);

  const m = measurements || {};

  const canSave = useMemo(() => {
    return (
      !!projectName.trim() &&
      !!canvasRef?.current &&
      typeof m.widthFt === "number" &&
      isFinite(m.widthFt) &&
      m.widthFt > 0 &&
      typeof m.lengthFt === "number" &&
      isFinite(m.lengthFt) &&
      m.lengthFt > 0
    );
  }, [projectName, canvasRef, m.widthFt, m.lengthFt]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setErrMsg(null);

    if (!projectName.trim()) {
      setErrMsg("Please enter a project name.");
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setErrMsg("Please sign in first.");
      return;
    }

    if (!canvasRef?.current) {
      setErrMsg("Canvas not available.");
      return;
    }

    setLoading(true);

    try {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);

      const safeBase =
        (surfaceName.trim() || projectName.trim())
          .replace(/[^a-z0-9]/gi, "_")
          .slice(0, 40) || "surface";

      const fileName = `${Date.now()}-${safeBase}.jpg`;

      // Upload annotated image to Node backend → Azure Blob
      const apiBase = API_BASE;

      const res = await fetch(`${apiBase}/api/upload-surface`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          imageBase64: dataUrl,
          fileName,
          userEmail: user.email,
          userId: user.uid,
        }),
      });

      const data = await res.json();
	    console.log("DEBUG RESPONSE:", res.ok, data);
      if (!res.ok) {
        throw new Error(data?.error || "Surface image upload failed.");
      }

      const surfaceUrl = data.surfaceUrl;
      const projectId = data.projectId;

      // ✅ IMPORTANT: write BOTH canonical + legacy fields
      // - Canonical: ownerUid/ownerEmail/createdAt
      // - Legacy/back-compat: userId/email/timestamp (AdminDashboard reads these)
      await addDoc(collection(db, "projects"), {
        projectType: "surface",

        // Canonical ownership
        ownerUid: user.uid,
        ownerEmail: user.email || null,

        // Back-compat (so AdminDashboard shows values)
        userId: user.uid,
        email: user.email || null,

        // Display fields
        projectName: projectName.trim(),
        surfaceName: surfaceName.trim() || null,

        // Optional contact/meta
        phone: phone.trim() || null,
        notes: notes.trim() || null,

        // Measurements
        aiWidthFt: m.widthFt ?? null,
        aiLengthFt: m.lengthFt ?? null,
        areaSqFt: m.areaSqFt ?? null,
        angleWidthDeg: m.angleWidthDeg ?? m.angleWidth ?? null,
        angleLengthDeg: m.angleLengthDeg ?? m.angleLength ?? null,
        calibrationSource: m.calibrationSource ?? null,
        scalePxPerInch: m.scalePxPerInch ?? m.scale ?? null,

        // Image
        surfaceUrl,

        // Timestamps
        createdAt: serverTimestamp(),
        timestamp: serverTimestamp(), // AdminDashboard sorts/filters on this

        // Sharing defaults
        sharedWithContractor: false,
        sharedAt: null,
        sharedByUid: null,
        sharedByEmail: null,

        // Workflow
        notifyContractors: true,
        status: "new",
      });

      setLoading(false);
      onClose(true);
    } catch (err) {
      console.error("❌ Surface project save error:", err);
      setLoading(false);
      setErrMsg(err?.message || "Saving failed (unknown error).");
      onClose(false);
    }
  };

  const styles = {
    backdrop: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000000,
      padding: 16,
    },
    modal: {
      width: "100%",
      maxWidth: 720,
      background: "#fff",
      borderRadius: 18,
      boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
      overflow: "hidden",
      border: "1px solid rgba(0,0,0,0.08)",
    },
    header: {
      padding: "18px 20px",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      background: "#f7f9ff",
    },
    title: { margin: 0, fontSize: 26, fontWeight: 900, color: "#0a4fa3" },
    subtitle: {
      margin: "10px 0 0",
      fontSize: 15,
      color: "#445",
      lineHeight: 1.5,
    },
    body: { padding: "18px 20px" },
    label: {
      display: "block",
      fontSize: 15,
      fontWeight: 900,
      marginBottom: 8,
      color: "#223",
    },
    help: { marginTop: 8, fontSize: 14, color: "#556", lineHeight: 1.5 },
    input: (danger) => ({
      width: "100%",
      padding: "14px 14px",
      borderRadius: 12,
      border: `2px solid ${danger ? "#c62828" : "rgba(0,0,0,0.18)"}`,
      outline: "none",
      fontSize: 16,
    }),
    textarea: {
      width: "100%",
      padding: "14px 14px",
      borderRadius: 12,
      border: "2px solid rgba(0,0,0,0.18)",
      outline: "none",
      fontSize: 16,
      resize: "vertical",
      minHeight: 140,
    },
    grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
    row: { marginBottom: 16 },
    error: {
      marginTop: 12,
      background: "#ffebee",
      border: "1px solid rgba(198,40,40,0.35)",
      color: "#b71c1c",
      padding: "12px 14px",
      borderRadius: 12,
      fontWeight: 800,
      fontSize: 14,
    },
    footer: {
      padding: "16px 20px",
      borderTop: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
      background: "#fff",
    },
    btn: (variant, enabled = true) => {
      const base = {
        padding: "12px 18px",
        borderRadius: 12,
        fontWeight: 900,
        cursor: enabled ? "pointer" : "not-allowed",
        border: "none",
        fontSize: 16,
        minWidth: 140,
      };
      if (variant === "cancel") {
        return { ...base, background: "#eceff1", color: "#223" };
      }
      return { ...base, background: enabled ? "#00897b" : "#b0bec5", color: "#fff" };
    },
    miniStats: {
      marginTop: 14,
      padding: "14px 16px",
      background: "#f6f8fa",
      borderRadius: 14,
      border: "1px solid rgba(0,0,0,0.06)",
      fontSize: 16,
      color: "#334",
      lineHeight: 1.65,
    },
    code: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 },
    statLabel: { fontWeight: 900, marginRight: 6 },
    tip: { marginTop: 10, fontSize: 14, color: "#556" },
  };

  const fmt = (n) => (typeof n === "number" && isFinite(n) ? n.toFixed(2) : "—");

  return (
    <div style={styles.backdrop} onMouseDown={() => !loading && onClose(false)}>
      <div
        style={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={styles.header}>
          <h2 style={styles.title}>Save Surface Project</h2>
          <p style={styles.subtitle}>
            This will upload your annotated image and post the project to the shared{" "}
            <span style={styles.code}>projects</span> collection so contractors can
            review it.
          </p>
        </div>

        <div style={styles.body}>
          <div style={styles.row}>
            <label style={styles.label}>Project Name *</label>
            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder='e.g., "Gonzalez – Window Trim (Front)"'
              style={styles.input(!!errMsg && !projectName.trim())}
            />
            <div style={styles.help}>Contractors see this first. Make it descriptive.</div>
          </div>

          <div style={styles.grid}>
            <div style={styles.row}>
              <label style={styles.label}>Surface / Area (optional)</label>
              <input
                value={surfaceName}
                onChange={(e) => setSurfaceName(e.target.value)}
                placeholder='e.g., "Kitchen window – sink wall"'
                style={styles.input(false)}
              />
            </div>

            <div style={styles.row}>
              <label style={styles.label}>Phone (optional)</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
                style={styles.input(false)}
              />
            </div>
          </div>

          <div style={styles.row}>
            <label style={styles.label}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Existing trim removal, match casing profile, prefer morning install."
              style={styles.textarea}
            />
          </div>

          <div style={styles.miniStats}>
            <div>
              <span style={styles.statLabel}>Width:</span> {fmt(m.widthFt)} ft
            </div>
            <div>
              <span style={styles.statLabel}>Length:</span> {fmt(m.lengthFt)} ft
            </div>
            <div>
              <span style={styles.statLabel}>Area:</span> {fmt(m.areaSqFt)} sq ft
            </div>
            <div style={styles.tip}>
              Tip: Save is enabled after Step 5 (both width + length).
            </div>
          </div>

          {errMsg && <div style={styles.error}>{errMsg}</div>}
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            disabled={loading}
            onClick={() => onClose(false)}
            style={styles.btn("cancel", !loading)}
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={loading || !canSave}
            onClick={handleSave}
            style={styles.btn("save", !loading && canSave)}
            title={canSave ? "Save and post to contractor dashboard" : "Complete Width + Length first"}
          >
            {loading ? "Saving..." : "Save Project"}
          </button>
        </div>
      </div>
    </div>
  );
}
